import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useDesignStore } from '../../store/designStore';
import { buildPreviewDataUrl } from '../../lib/designDownload';
import { EmbroideryPreviewStatus, isEmbroideryPrintMethod, EmbossingPreviewStatus, isEmbossingPrintMethod, LaserEngravingPreviewStatus, isLaserEngravingPrintMethod, DomingPreviewStatus, isDomingPrintMethod, SublimationPreviewStatus, isSublimationPrintMethod } from '@customedge/shared';
import { safeGetItem } from '../../lib/safeStorage';

type ViewMode = 'grid' | 'slider';

function authHeaders(): Record<string, string> {
  const token = safeGetItem('session', 'customedge_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Polls GET /api/designs/:id for the saved design's embroidery-preview
// status/images — same "poll a DB status column via React Query
// refetchInterval" convention the admin app already uses (DesignWip.tsx,
// Orders.tsx), applied here for the first time on the customer-facing side.
// Only fetches at all once a save has happened (savedDesignId set) — for a
// design that was never saved with embroidery preview enabled/matching, this
// stays permanently disabled and costs nothing beyond the one-time enabled
// check below.
function useEmbroideryPreviewStatus(savedDesignId: string | null, savedDesignVersion: number) {
  return useQuery({
    // REAL BUG found+fixed 2026-08-21 (live user report: editing text and
    // saving again — Save button, Next/Finish, or just waiting for
    // autosave — after the Preview tab had already shown a finished render
    // once left it stuck on the OLD image forever): `refetchInterval` below
    // only resumes polling based on the CACHED status from the previous
    // fetch — once that cache says DONE, it returns `false` and never
    // fetches again, even after a genuinely new save re-queues a fresh
    // render server-side. Including `savedDesignVersion` (bumped on every
    // successful save — see designStore.ts's own comment on that field) in
    // the query key forces React Query to treat a new save as a distinct
    // query, triggering an immediate real fetch and re-establishing the
    // polling loop from a fresh (correct) status instead of trusting
    // indefinitely-stale cached data.
    queryKey: ['embroidery-preview-status', savedDesignId, savedDesignVersion],
    queryFn: () => fetch(`/api/designs/${savedDesignId}`, { headers: authHeaders() }).then(r => r.ok ? r.json() : null),
    enabled: !!savedDesignId,
    // Poll only while a render could still be in flight — QUEUED/PROCESSING.
    // Stops polling once DONE/FAILED/NONE (a status query that never changes
    // again), or while the fetch hasn't landed yet (data undefined — the
    // first fetch itself isn't a "keep polling" signal, just a normal
    // one-shot load).
    refetchInterval: (query) => {
      const status = query.state.data?.embroideryPreviewStatus;
      return status === EmbroideryPreviewStatus.QUEUED || status === EmbroideryPreviewStatus.PROCESSING ? 4000 : false;
    },
  });
}

// Structural copy of useEmbroideryPreviewStatus above, polling the same
// GET /api/designs/:id response for the embossing-preview status/images
// instead — the response already carries both, so this is a second React
// Query cache entry over the same fetch, not a second network call pattern
// (React Query dedupes identical in-flight queryFns by key; the two hooks
// use different keys so each gets its own cached copy of the same response,
// which is an acceptable, simple cost for keeping the two preview types'
// polling logic independent and equally easy to reason about).
function useEmbossingPreviewStatus(savedDesignId: string | null, savedDesignVersion: number) {
  return useQuery({
    queryKey: ['embossing-preview-status', savedDesignId, savedDesignVersion],
    queryFn: () => fetch(`/api/designs/${savedDesignId}`, { headers: authHeaders() }).then(r => r.ok ? r.json() : null),
    enabled: !!savedDesignId,
    refetchInterval: (query) => {
      const status = query.state.data?.embossingPreviewStatus;
      return status === EmbossingPreviewStatus.QUEUED || status === EmbossingPreviewStatus.PROCESSING ? 4000 : false;
    },
  });
}

// Structural copy of useEmbossingPreviewStatus above, polling the same
// GET /api/designs/:id response for the laser-engraving-preview status/
// images instead — third independent React Query cache entry over the same
// underlying fetch (see useEmbossingPreviewStatus's own comment on why this
// is an acceptable, simple cost).
function useLaserEngravingPreviewStatus(savedDesignId: string | null, savedDesignVersion: number) {
  return useQuery({
    queryKey: ['laser-engraving-preview-status', savedDesignId, savedDesignVersion],
    queryFn: () => fetch(`/api/designs/${savedDesignId}`, { headers: authHeaders() }).then(r => r.ok ? r.json() : null),
    enabled: !!savedDesignId,
    refetchInterval: (query) => {
      const status = query.state.data?.laserEngravingPreviewStatus;
      return status === LaserEngravingPreviewStatus.QUEUED || status === LaserEngravingPreviewStatus.PROCESSING ? 4000 : false;
    },
  });
}

// Structural copy of useLaserEngravingPreviewStatus above, polling the same
// GET /api/designs/:id response for the doming-preview status/images
// instead — fourth independent React Query cache entry over the same
// underlying fetch (see useEmbossingPreviewStatus's own comment on why this
// is an acceptable, simple cost).
function useDomingPreviewStatus(savedDesignId: string | null, savedDesignVersion: number) {
  return useQuery({
    queryKey: ['doming-preview-status', savedDesignId, savedDesignVersion],
    queryFn: () => fetch(`/api/designs/${savedDesignId}`, { headers: authHeaders() }).then(r => r.ok ? r.json() : null),
    enabled: !!savedDesignId,
    refetchInterval: (query) => {
      const status = query.state.data?.domingPreviewStatus;
      return status === DomingPreviewStatus.QUEUED || status === DomingPreviewStatus.PROCESSING ? 4000 : false;
    },
  });
}

// Structural copy of useDomingPreviewStatus above, polling the same
// GET /api/designs/:id response for the sublimation-preview status/images
// instead — fifth independent React Query cache entry over the same
// underlying fetch (see useEmbossingPreviewStatus's own comment on why this
// is an acceptable, simple cost).
function useSublimationPreviewStatus(savedDesignId: string | null, savedDesignVersion: number) {
  return useQuery({
    queryKey: ['sublimation-preview-status', savedDesignId, savedDesignVersion],
    queryFn: () => fetch(`/api/designs/${savedDesignId}`, { headers: authHeaders() }).then(r => r.ok ? r.json() : null),
    enabled: !!savedDesignId,
    refetchInterval: (query) => {
      const status = query.state.data?.sublimationPreviewStatus;
      return status === SublimationPreviewStatus.QUEUED || status === SublimationPreviewStatus.PROCESSING ? 4000 : false;
    },
  });
}

// Read-only "what the customer gets" view for the Preview tab — the design composited
// onto the full product photo at its real saved position, with none of the editing
// chrome (selection handles, design-area guide, tool panels). Builds every side's
// composite up front so both the grid overview and the slider can switch instantly.
//
// `initialViewMode` defaults to 'grid' (the designer's own Preview tab, which has
// full page height to work with). The customer approval page's embedded preview
// (DesignPreviewCanvas → ApprovalPreview) passes 'slider' instead: that context
// runs inside a short, height-capped column (see ApprovalPage.tsx's mobile layout),
// and grid mode's CSS Grid + `overflow: auto` combination has a real, reproducible
// Chromium quirk at that narrow initial viewport width — the grid's own scrollable
// clip doesn't apply on first paint (only after a later resize/reflow), so an
// over-tall grid tile visibly bleeds past the column into whatever's below it.
// Slider mode sidesteps the whole class of bug: it sizes via plain flex +
// `max-height: 100%`, which was verified correct in every configuration tested.
interface PreviewViewProps {
  initialViewMode?: ViewMode;
  autoAdvanceMs?: number;
  hideChrome?: boolean;
  /** Suppresses this component's own grid/slider toggle row — for
   *  DesignPreviewCanvas, which renders the same grid/slider control itself
   *  (as small icon buttons merged into its own "2D Preview / 3D Preview"
   *  row) instead of a second row here. Requires viewMode/onViewModeChange
   *  below so the caller can still drive which mode is active. */
  hideModeToggle?: boolean;
  /** Controlled viewMode — only used together with hideModeToggle. When
   *  absent, this component manages viewMode itself (DesignerApp's own
   *  Preview tab, which owns and renders the toggle internally). */
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

export function PreviewView({ initialViewMode = 'grid', autoAdvanceMs, hideChrome = false, hideModeToggle = false, viewMode: viewModeProp, onViewModeChange }: PreviewViewProps = {}) {
  const { t } = useTranslation();
  const {
    sides, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey, variants, selectedVariantOptions, savedDesignId,
    selectedPrintMethod, selectedPrintMethodByPosition, savedDesignVersion, dirtySideKeys,
  } = useDesignStore();
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const { data: embroideryDetail, isFetching: isFetchingEmbroideryDetail } = useEmbroideryPreviewStatus(savedDesignId, savedDesignVersion);
  const { data: embossingDetail, isFetching: isFetchingEmbossingDetail } = useEmbossingPreviewStatus(savedDesignId, savedDesignVersion);
  const { data: laserEngravingDetail, isFetching: isFetchingLaserEngravingDetail } = useLaserEngravingPreviewStatus(savedDesignId, savedDesignVersion);
  const { data: domingDetail, isFetching: isFetchingDomingDetail } = useDomingPreviewStatus(savedDesignId, savedDesignVersion);
  const { data: sublimationDetail, isFetching: isFetchingSublimationDetail } = useSublimationPreviewStatus(savedDesignId, savedDesignVersion);
  // Only swap in the real digitized render for sides whose print method
  // actually matched at save/render time — embroideryPreviewImagesBySideKey
  // (see GET /api/designs/:id) only ever contains entries for sides the
  // service actually rendered, so simple key presence is the correct gate;
  // no separate per-side method check needed here.
  //
  // REAL BUG found+fixed 2026-08-21 (live user report, multi-position CX1534
  // design): this used to ALSO require the whole-design
  // embroideryPreviewStatus === DONE before using ANY of this map — but
  // status is a single design-wide field while embroideryPreviewImagesBySideKey
  // is genuinely per-side. For a design with multiple embroidery sides (or
  // one that gets re-saved/re-queued while the user is still looking, see
  // designs.ts's own de-dupe fix from the same report), the design-wide
  // status can sit at QUEUED/PROCESSING for a side whose OWN render already
  // finished and is sitting right there in the map — that side's real
  // finished image was being discarded and silently falling back to the
  // plain client-side render, which looked like "this position isn't
  // updating" even though the correct image already existed. Fixed to just
  // use the map directly: per the comment above, key presence already means
  // "this specific side's render is done," independent of what any OTHER
  // side (or a newer in-flight job) is doing.
  const embroideryImagesBySideKey: Record<string, string> = embroideryDetail?.embroideryPreviewImagesBySideKey ?? {};
  // Embossing's equivalent map — same key-presence-is-the-gate rationale as
  // embroidery's above. A side resolves to exactly one print method, so
  // embroidery's, embossing's, laser engraving's, doming's, and
  // sublimation's maps can never both have an entry for the same sideKey;
  // the render below tries embroidery first, then embossing, then laser
  // engraving, then doming, then sublimation. That fixed try-order is only
  // ever a tie-break between branches that structurally cannot both be true
  // for the same side — unlike basePreviewRefresh.ts's OWN now-fixed bug
  // (see that file's header comment), where a fixed type-priority order
  // used to pick the wrong winner ACROSS SIDES on the same design. This
  // chain never faces that failure mode because it operates per-sideKey,
  // not per-design: at most one of the 5 checks below can ever be true for
  // a given side, so which one is checked first has no observable effect.
  const embossingImagesBySideKey: Record<string, string> = embossingDetail?.embossingPreviewImagesBySideKey ?? {};
  // Laser engraving's equivalent map — same key-presence-is-the-gate
  // rationale as embroidery's/embossing's above.
  const laserEngravingImagesBySideKey: Record<string, string> = laserEngravingDetail?.laserEngravingPreviewImagesBySideKey ?? {};
  // Doming's equivalent map — same key-presence-is-the-gate rationale as the
  // other three above.
  const domingImagesBySideKey: Record<string, string> = domingDetail?.domingPreviewImagesBySideKey ?? {};
  // Sublimation's equivalent map — same key-presence-is-the-gate rationale
  // as the other four above.
  const sublimationImagesBySideKey: Record<string, string> = sublimationDetail?.sublimationPreviewImagesBySideKey ?? {};
  // Per-side "still digitizing" flag — added 2026-08-21 per a live user
  // report ("loading normal then changing after some seconds"): a real
  // embroidery render takes 10-30+ seconds (Inkscape/Ink-Stitch shell-out,
  // not instant), and until now the Preview tab showed the plain flat-text
  // client render with NOTHING indicating a better image was still on its
  // way — it just silently swapped once ready, which reads as "the preview
  // is wrong" rather than "the preview is still loading" to anyone watching.
  // Mirrors the SAME per-side print-method resolution
  // embroideryPreviewService.ts's own per-side loop uses (position override,
  // falling back to the whole-design method) so this can never disagree with
  // what the backend is actually about to render for a given side.
  const embroideryStatus = embroideryDetail?.embroideryPreviewStatus;
  // REAL BUG found+fixed 2026-08-21 (live user report: switching to Preview/
  // Slider while a render was genuinely in flight showed no loading badge at
  // all): this used to gate SOLELY on the query's last-fetched status
  // equaling QUEUED/PROCESSING — but right after a save (which bumps
  // savedDesignVersion, giving this query a fresh key), there's a real
  // window where `embroideryDetail` is still `undefined` because the new
  // fetch hasn't resolved yet, during which `embroideryStatus` reads
  // `undefined` and the old check silently evaluated to "not pending," even
  // though a render was almost certainly already queued/running server-side
  // by the time the user switched tabs. Fixed by ALSO showing the badge
  // while the query itself is actively fetching (`isFetching`, true for
  // both the very first load of a fresh query key AND every subsequent
  // refetchInterval poll) — closes the gap between "a new render was just
  // triggered" and "the first status fetch for it has actually landed,"
  // not just the already-correctly-handled QUEUED/PROCESSING window after.
  const isEmbroideryRenderInFlight =
    isFetchingEmbroideryDetail ||
    embroideryStatus === EmbroideryPreviewStatus.QUEUED ||
    embroideryStatus === EmbroideryPreviewStatus.PROCESSING;
  // REAL BUG found+fixed 2026-08-27 (live user report: editing a position
  // and immediately clicking Next/opening this tab kept showing that side's
  // STALE render with no indication anything was wrong — see
  // designStore.ts's own comment on dirtySideKeys for the full mechanism).
  // A side counts as pending if EITHER the server says a render is actually
  // in flight (existing check above) OR the customer has edited it locally
  // since the last save (dirtySideKeys) — the latter needs no server
  // round-trip at all to know the cached image can no longer be trusted.
  const pendingEmbroiderySideKeys = new Set(
    sides
      .filter(side => {
        const positionMethod = side.printPosition ? selectedPrintMethodByPosition[side.printPosition] : undefined;
        const method = positionMethod ?? selectedPrintMethod;
        if (!isEmbroideryPrintMethod(method)) return false;
        if (dirtySideKeys.has(side.sideKey)) return true;
        return isEmbroideryRenderInFlight && !embroideryImagesBySideKey[side.sideKey];
      })
      .map(side => side.sideKey)
  );
  // Embossing's equivalent in-flight flag/pending-set — same structure as
  // embroidery's above, applied to isEmbossingPrintMethod/embossingDetail.
  const embossingStatus = embossingDetail?.embossingPreviewStatus;
  const isEmbossingRenderInFlight =
    isFetchingEmbossingDetail ||
    embossingStatus === EmbossingPreviewStatus.QUEUED ||
    embossingStatus === EmbossingPreviewStatus.PROCESSING;
  const pendingEmbossingSideKeys = new Set(
    sides
      .filter(side => {
        const positionMethod = side.printPosition ? selectedPrintMethodByPosition[side.printPosition] : undefined;
        const method = positionMethod ?? selectedPrintMethod;
        if (!isEmbossingPrintMethod(method)) return false;
        if (dirtySideKeys.has(side.sideKey)) return true;
        return isEmbossingRenderInFlight && !embossingImagesBySideKey[side.sideKey];
      })
      .map(side => side.sideKey)
  );
  // Laser engraving's equivalent in-flight flag/pending-set — same structure
  // as embroidery's/embossing's above, applied to
  // isLaserEngravingPrintMethod/laserEngravingDetail.
  const laserEngravingStatus = laserEngravingDetail?.laserEngravingPreviewStatus;
  const isLaserEngravingRenderInFlight =
    isFetchingLaserEngravingDetail ||
    laserEngravingStatus === LaserEngravingPreviewStatus.QUEUED ||
    laserEngravingStatus === LaserEngravingPreviewStatus.PROCESSING;
  const pendingLaserEngravingSideKeys = new Set(
    sides
      .filter(side => {
        const positionMethod = side.printPosition ? selectedPrintMethodByPosition[side.printPosition] : undefined;
        const method = positionMethod ?? selectedPrintMethod;
        if (!isLaserEngravingPrintMethod(method)) return false;
        if (dirtySideKeys.has(side.sideKey)) return true;
        return isLaserEngravingRenderInFlight && !laserEngravingImagesBySideKey[side.sideKey];
      })
      .map(side => side.sideKey)
  );
  // Doming's equivalent in-flight flag/pending-set — same structure as
  // embroidery's/embossing's/laser engraving's above, applied to
  // isDomingPrintMethod/domingDetail.
  const domingStatus = domingDetail?.domingPreviewStatus;
  const isDomingRenderInFlight =
    isFetchingDomingDetail ||
    domingStatus === DomingPreviewStatus.QUEUED ||
    domingStatus === DomingPreviewStatus.PROCESSING;
  const pendingDomingSideKeys = new Set(
    sides
      .filter(side => {
        const positionMethod = side.printPosition ? selectedPrintMethodByPosition[side.printPosition] : undefined;
        const method = positionMethod ?? selectedPrintMethod;
        if (!isDomingPrintMethod(method)) return false;
        if (dirtySideKeys.has(side.sideKey)) return true;
        return isDomingRenderInFlight && !domingImagesBySideKey[side.sideKey];
      })
      .map(side => side.sideKey)
  );
  // Sublimation's equivalent in-flight flag/pending-set — same structure as
  // embroidery's/embossing's/laser engraving's/doming's above, applied to
  // isSublimationPrintMethod/sublimationDetail.
  const sublimationStatus = sublimationDetail?.sublimationPreviewStatus;
  const isSublimationRenderInFlight =
    isFetchingSublimationDetail ||
    sublimationStatus === SublimationPreviewStatus.QUEUED ||
    sublimationStatus === SublimationPreviewStatus.PROCESSING;
  const pendingSublimationSideKeys = new Set(
    sides
      .filter(side => {
        const positionMethod = side.printPosition ? selectedPrintMethodByPosition[side.printPosition] : undefined;
        const method = positionMethod ?? selectedPrintMethod;
        if (!isSublimationPrintMethod(method)) return false;
        if (dirtySideKeys.has(side.sideKey)) return true;
        return isSublimationRenderInFlight && !sublimationImagesBySideKey[side.sideKey];
      })
      .map(side => side.sideKey)
  );
  const [viewModeState, setViewModeState] = useState<ViewMode>(initialViewMode);
  // Controlled (hideModeToggle callers) vs. uncontrolled (DesignerApp's own
  // Preview tab) — same dual-mode pattern as a native <input>.
  const viewMode = hideModeToggle && viewModeProp ? viewModeProp : viewModeState;
  const setViewMode = hideModeToggle && onViewModeChange ? onViewModeChange : setViewModeState;
  const [sliderIndex, setSliderIndex] = useState(0);

  // Object Variants — a variant targets a 2D side via `imageSideId` (the
  // admin sets this explicitly per Variant, since a mesh name alone doesn't
  // imply a 2D side). Custom free-picked colors (`custom:#hex`) have no
  // uploaded preview image, so they only affect the 3D tab, not this overlay.
  const variantOverlayUrlBySideKey: Record<string, string> = {};
  for (const side of sides) {
    const variant = variants.find(v => v.imageSideId === side.photoId);
    if (!variant) continue;
    const selection = selectedVariantOptions[variant.id];
    if (!selection || selection.startsWith('custom:')) continue;
    const option = variant.options.find(o => o.id === selection);
    if (option?.previewImageUrl) variantOverlayUrlBySideKey[side.sideKey] = option.previewImageUrl;
  }
  const variantOverlayKey = Object.entries(variantOverlayUrlBySideKey).map(([k, v]) => `${k}:${v}`).join('|');
  const embroideryImagesKey = Object.entries(embroideryImagesBySideKey).map(([k, v]) => `${k}:${v}`).join('|');
  const embossingImagesKey = Object.entries(embossingImagesBySideKey).map(([k, v]) => `${k}:${v}`).join('|');
  const laserEngravingImagesKey = Object.entries(laserEngravingImagesBySideKey).map(([k, v]) => `${k}:${v}`).join('|');
  const domingImagesKey = Object.entries(domingImagesBySideKey).map(([k, v]) => `${k}:${v}`).join('|');
  const sublimationImagesKey = Object.entries(sublimationImagesBySideKey).map(([k, v]) => `${k}:${v}`).join('|');
  const dirtySideKeysKey = Array.from(dirtySideKeys).sort().join('|');

  useEffect(() => {
    if (!sides.length) return;
    let cancelled = false;
    setLoading(true);
    // Bounded concurrency (2 sides at a time), not Promise.all(sides.map(...)) —
    // each side's own buildPreviewDataUrl already fires 2-3 concurrent image
    // loads (photo + design + optional variant overlay), so with 6 sides an
    // unbounded Promise.all() here could burst 12-18 simultaneous CORS-mode
    // fetches to the same cross-origin S3 host at once. Confirmed live
    // 2026-08-13: that burst intermittently failed a subset of requests with
    // "No Access-Control-Allow-Origin header" even though the same URLs
    // loaded fine both individually (curl, 8x back-to-back, 0 failures) and
    // one-at-a-time in the Design tab — a browser-side concurrent-connection
    // race under load, not a real CORS policy problem. A small concurrency
    // cap keeps this well under whatever limit was being hit without making
    // the tab noticeably slower to fill in (2 in flight, not fully serial).
    const PREVIEW_CONCURRENCY = 2;
    async function loadWithLimit() {
      const results: Array<readonly [string, string | null]> = [];
      let nextIndex = 0;
      async function worker() {
        while (true) {
          const i = nextIndex++;
          if (i >= sides.length) return;
          const side = sides[i];
          // A side edited since the last save (see designStore.ts's
          // dirtySideKeys comment) must NOT use its cached embroidery/
          // embossing/laser-engraving image below — that image reflects
          // the OLD, pre-edit content. Force a fresh plain client-side
          // composite instead, which reflects the live canvas right now;
          // the pendingXSideKeys loading badge (see those Sets above) then
          // renders on TOP of this fresh composite until the real
          // server-side re-render finishes and the cached image map itself
          // updates (at which point this side is no longer dirty and falls
          // through to the cached-image shortcuts below again).
          if (!dirtySideKeys.has(side.sideKey)) {
            // A ready, cached embroidery-style render already covers this side
            // (see embroideryImagesBySideKey above) — skip the client-side
            // Fabric composite entirely rather than building it only to
            // discard it in favor of the embroidery image below. Still counts
            // as "loaded" for this side (results[i] stays populated), so the
            // grid/slider don't show a loading state for it.
            if (embroideryImagesBySideKey[side.sideKey]) {
              results[i] = [side.sideKey, embroideryImagesBySideKey[side.sideKey]];
              continue;
            }
            // Embossing's equivalent skip — same rationale as embroidery's
            // above (a ready render already covers this side, don't build a
            // client-side composite only to discard it).
            if (embossingImagesBySideKey[side.sideKey]) {
              results[i] = [side.sideKey, embossingImagesBySideKey[side.sideKey]];
              continue;
            }
            // Laser engraving's equivalent skip — same rationale as
            // embroidery's/embossing's above.
            if (laserEngravingImagesBySideKey[side.sideKey]) {
              results[i] = [side.sideKey, laserEngravingImagesBySideKey[side.sideKey]];
              continue;
            }
            // Doming's equivalent skip — same rationale as the other three
            // above.
            if (domingImagesBySideKey[side.sideKey]) {
              results[i] = [side.sideKey, domingImagesBySideKey[side.sideKey]];
              continue;
            }
            // Sublimation's equivalent skip — same rationale as the other
            // four above.
            if (sublimationImagesBySideKey[side.sideKey]) {
              results[i] = [side.sideKey, sublimationImagesBySideKey[side.sideKey]];
              continue;
            }
          }
          try {
            const url = await buildPreviewDataUrl(side, canvasInstances, sideColorCountJsonCache, resolveActiveColorCountKey, variantOverlayUrlBySideKey[side.sideKey]);
            results[i] = [side.sideKey, url];
          } catch {
            results[i] = [side.sideKey, null];
          }
        }
      }
      await Promise.all(Array.from({ length: Math.min(PREVIEW_CONCURRENCY, sides.length) }, worker));
      return results;
    }
    loadWithLimit().then(entries => {
      if (cancelled) return;
      const map: Record<string, string> = {};
      for (const [key, url] of entries) if (url) map[key] = url;
      setPreviews(map);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // `canvasInstances`/`sideColorCountJsonCache` deliberately included, not just
    // `sides`: opening this tab unmounts DesignerWorkspace, whose cleanup effect is
    // what populates the cache for the side just switched away from — that cleanup
    // and this component's mount effect are both passive effects with no ordering
    // guarantee, so without depending on the cache itself this could run once against
    // the still-empty pre-cleanup value and never re-run once the real content lands.
    // `variantOverlayKey` (not the object itself) is the real dependency — a fresh
    // object reference is built every render even when nothing relevant changed.
    // `embroideryImagesKey` (same reasoning) re-runs this effect once polling
    // (useEmbroideryPreviewStatus above) transitions from "not ready" to a
    // real set of rendered image URLs, so the embroidery-style render swaps
    // in without the customer needing to leave and reopen the Preview tab.
    // `embossingImagesKey`/`laserEngravingImagesKey`/`domingImagesKey`/
    // `sublimationImagesKey` are the same pattern for their own preview
    // types. `dirtySideKeysKey` re-runs this effect both when a side FIRST
    // becomes dirty (so its stale cached image is dropped in favor of a
    // fresh client composite, per the loadWithLimit skip-guard above) and
    // when it's later cleared by a successful save (so it can go back to
    // using the cached image once a fresh one lands).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sides, canvasInstances, sideColorCountJsonCache, variantOverlayKey, embroideryImagesKey, embossingImagesKey, laserEngravingImagesKey, domingImagesKey, sublimationImagesKey, dirtySideKeysKey]);

  const openInSlider = (index: number) => {
    setSliderIndex(index);
    setViewMode('slider');
  };

  const goPrev = useCallback(() => setSliderIndex(i => (i - 1 + sides.length) % sides.length), [sides.length]);
  const goNext = useCallback(() => setSliderIndex(i => (i + 1) % sides.length), [sides.length]);

  useEffect(() => {
    if (viewMode !== 'slider') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewMode, goPrev, goNext]);

  // Opt-in auto-advance for read-only, unattended contexts (the PDP's floating
  // "keep your saved design in sight" popover) — the designer's own Preview
  // tab and the widget/approval preview never pass autoAdvanceMs, so they
  // keep their existing manual-only behavior.
  useEffect(() => {
    if (viewMode !== 'slider' || !autoAdvanceMs || sides.length <= 1) return;
    const id = window.setInterval(goNext, autoAdvanceMs);
    return () => window.clearInterval(id);
  }, [viewMode, autoAdvanceMs, sides.length, goNext]);

  return (
    // `minHeight: 0` here matters whenever this mounts inside a height-
    // constrained flex ancestor (e.g. the customer approval page's preview
    // column, which caps this to a fixed height on mobile) — without it, a
    // flex item defaults to `min-height: auto`, which uses its CONTENT's
    // natural size as a floor and ignores `flex: 1`/the parent's own height,
    // so the grid-mode image grid below (whose images want up to 200px each)
    // could grow taller than this container and paint past this element's
    // own `overflow: hidden`, bleeding into whatever sits after it in the
    // page (confirmed via a ruler screenshot: the clipped bounding box was
    // correct, but content still visibly overflowed it).
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff', position: 'relative', minHeight: 0 }}>
      {/* View mode toggle — omitted in hideChrome (compact, unattended contexts
          like the PDP floating popover have no room for it and only ever run
          in slider mode anyway, set via initialViewMode) AND in hideModeToggle
          (DesignPreviewCanvas renders this same control itself, as icon
          buttons merged into its own "2D Preview / 3D Preview" row instead of
          a second row here). The finish/save action itself lives in the
          header (DesignerHeader's "Finish Design" step), not in this row. */}
      {!hideChrome && !hideModeToggle && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 16px', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 4 }}>
            {(['grid', 'slider'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                style={{
                  padding: '6px 16px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 20,
                  background: viewMode === mode ? 'var(--selection)' : '#fff',
                  color: viewMode === mode ? '#fff' : '#374151',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {mode === 'grid' ? t('designer.previewGrid') : t('designer.previewSlider')}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: '#94a3b8', fontSize: 14 }}>{t('designer.previewLoading')}</span>
        </div>
      )}

      {!loading && viewMode === 'grid' && (
        <div style={{
          flex: 1, minHeight: 0, overflow: 'auto', padding: 24,
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20,
          alignContent: 'start',
        }}>
          {sides.map((side, i) => (
            <button
              key={side.sideKey}
              onClick={() => openInSlider(i)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: 16, cursor: 'pointer',
              }}
            >
              {previews[side.sideKey]
                ? (
                  <div style={{ position: 'relative', maxWidth: '100%' }}>
                    <img src={previews[side.sideKey]} alt="" style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', display: 'block' }} />
                    {pendingEmbroiderySideKeys.has(side.sideKey) && <PreviewLoadingBadge label={t('designer.embroideryDigitizing')} />}
                    {pendingEmbossingSideKeys.has(side.sideKey) && <PreviewLoadingBadge label={t('designer.embossingRendering')} />}
                    {pendingLaserEngravingSideKeys.has(side.sideKey) && <PreviewLoadingBadge label={t('designer.laserEngravingRendering')} />}
                    {pendingDomingSideKeys.has(side.sideKey) && <PreviewLoadingBadge label={t('designer.domingRendering')} />}
                    {pendingSublimationSideKeys.has(side.sideKey) && <PreviewLoadingBadge label={t('designer.sublimationRendering')} />}
                  </div>
                )
                : <span style={{ color: '#94a3b8', fontSize: 12 }}>{t('designer.previewFailed')}</span>}
              <span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>{side.label}</span>
            </button>
          ))}
        </div>
      )}

      {!loading && viewMode === 'slider' && sides.length > 0 && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', padding: hideChrome ? '13px 8px' : '29px 24px', overflow: 'hidden', minHeight: 0 }}>
          {!hideChrome && sides.length > 1 && (
            <button onClick={goPrev} style={navBtnStyle('left')} aria-label={t('designer.previewPrev')}>‹</button>
          )}

          {/* Dots row (+ caption below it, non-compact only) sits below the image
              and has real height — without a matching spacer above, centering the
              image within the remaining space (rather than the full container)
              visually shifts it toward the top, making the gray backdrop look
              uneven above vs. below the image. This spacer mirrors that height so
              the image itself lands in the true visual center. */}
          <div aria-hidden style={{ visibility: 'hidden', flexShrink: 0, display: 'flex', gap: hideChrome ? 5 : 8, marginBottom: hideChrome ? 6 : 16 }}>
            {sides.map(side => (
              <div key={side.sideKey} style={{ width: hideChrome ? 6 : 9, height: hideChrome ? 6 : 9 }} />
            ))}
          </div>
          {!hideChrome && <span aria-hidden style={{ fontSize: 12, visibility: 'hidden', flexShrink: 0, marginBottom: 6 }}>&nbsp;</span>}

          {/* flex:1 + minHeight:0 so the image is the one thing that shrinks
              under height pressure — the dots/label footer below is flexShrink:0
              and always keeps its own reserved space, so the label can never get
              clipped by this container's own `overflow: hidden` the way it did
              when everything shared one undifferentiated centered flex column
              (confirmed live: at the search.promofit.nl "Design Preview" popup's
              actual iframe height, the label was fully cut off below the fold). */}
          <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '100%' }}>
            {previews[sides[sliderIndex].sideKey]
              ? (
                // REAL BUG found+fixed 2026-08-21 (live user reports, three
                // rounds on this same wrapper div): wrapping the <img> in a
                // wrapper (for the EmbroideryLoadingBadge overlay) broke
                // sizing three ways in a row. Round 1 (`display` unset,
                // `maxHeight:'100%'`): no defined height of its own to
                // resolve the percentage against, so the wrapper grew to the
                // image's full intrinsic size and got clipped by this
                // component's own `overflow:hidden`. Round 2
                // (`display:'flex'`): gave the wrapper a real height, but
                // made the <img> a flex ITEM, which stretches along the
                // cross axis by default — visibly distorted the image wider/
                // flatter than its real proportions. Round 3
                // (`display:'inline-block'` + `object-fit:'contain'`):
                // fixed the distortion (confirmed via exact pixel
                // measurement, displayed size == natural size) but
                // `inline-block` STILL has no definite height of its own —
                // this time the image's `maxWidth:100%` happened to be the
                // binding constraint given this particular portrait-aspect
                // photo, which produced a computed height that passed an
                // 800px-viewport test but genuinely overflowed a realistic
                // browser window, silently pushing the bottom of the image
                // (where the actual embroidered text/design sits) below the
                // fold with no scroll affordance — read as "the position is
                // hidden" by the user, confirmed via a real screenshot.
                // Fixed by giving the <img> a definite `height:'100%'`
                // (not `maxHeight`) so it's FORCED to exactly fill the
                // ancestor flex container's real computed height (which
                // genuinely has one — `flex:1, minHeight:0` a few lines up
                // in this same render, inside a real flex column) — `width:
                // 'auto'` + `objectFit:'contain'` + `maxWidth:'100%'` as a
                // safety net for narrow viewports where width becomes
                // binding instead. This guarantees the WHOLE image always
                // fits the visible area with correct proportions, rather
                // than depending on which dimension happens to be binding
                // for a given photo's aspect ratio.
                <div style={{ position: 'relative', height: '100%', maxWidth: '100%', display: 'flex', justifyContent: 'center' }}>
                  <img src={previews[sides[sliderIndex].sideKey]} alt={sides[sliderIndex].label} style={{ height: '100%', width: 'auto', maxWidth: '100%', objectFit: 'contain', display: 'block', boxShadow: hideChrome ? 'none' : '0 2px 12px rgba(0,0,0,0.12)' }} />
                  {pendingEmbroiderySideKeys.has(sides[sliderIndex].sideKey) && <PreviewLoadingBadge label={t('designer.embroideryDigitizing')} />}
                  {pendingEmbossingSideKeys.has(sides[sliderIndex].sideKey) && <PreviewLoadingBadge label={t('designer.embossingRendering')} />}
                  {pendingLaserEngravingSideKeys.has(sides[sliderIndex].sideKey) && <PreviewLoadingBadge label={t('designer.laserEngravingRendering')} />}
                  {pendingDomingSideKeys.has(sides[sliderIndex].sideKey) && <PreviewLoadingBadge label={t('designer.domingRendering')} />}
                  {pendingSublimationSideKeys.has(sides[sliderIndex].sideKey) && <PreviewLoadingBadge label={t('designer.sublimationRendering')} />}
                </div>
              )
              : <span style={{ color: '#94a3b8', fontSize: 14 }}>{t('designer.previewFailed')}</span>}
          </div>

          {!hideChrome && sides.length > 1 && (
            <button onClick={goNext} style={navBtnStyle('right')} aria-label={t('designer.previewNext')}>›</button>
          )}

          <div style={{ flexShrink: 0, display: 'flex', gap: hideChrome ? 5 : 8, marginTop: hideChrome ? 6 : 16 }}>
            {sides.map((side, i) => (
              <button
                key={side.sideKey}
                onClick={() => setSliderIndex(i)}
                title={side.label}
                style={{
                  width: hideChrome ? 6 : 9, height: hideChrome ? 6 : 9, borderRadius: '50%', border: 'none', cursor: hideChrome ? 'default' : 'pointer', padding: 0,
                  background: i === sliderIndex ? 'var(--selection)' : '#cbd5e1',
                }}
              />
            ))}
          </div>
          {!hideChrome && (
            <span style={{ flexShrink: 0, fontSize: 12, color: '#64748b', marginTop: 6 }}>{sides[sliderIndex].label} — {sliderIndex + 1}/{sides.length}</span>
          )}
        </div>
      )}
    </div>
  );
}

// Small spinner overlay shown on top of a side's preview image while its
// real digitized render (embroidery, embossing, OR laser engraving) is
// still in flight — see pendingEmbroiderySideKeys/pendingEmbossingSideKeys/
// pendingLaserEngravingSideKeys above for the full
// rationale. Sits over the PLAIN client-side render (which is still shown
// underneath, not hidden) rather than blocking the whole tile, so the
// customer sees *something* immediately plus a clear signal that a better
// image is still coming, instead of the silent flat-then-swapped render
// that read as "broken." Generic name/parameterized label (originally
// embroidery-only) so both preview types share one spinner implementation.
function PreviewLoadingBadge({ label }: { label: string }) {
  return (
    <div
      style={{
        position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 8,
        // REAL BUG found+fixed 2026-08-27 (live user report: opening the
        // Preview tab for an ALREADY-FINISHED embossing/embroidery/
        // laser-engraving render briefly showed the plain flat text through
        // this badge's own semi-transparent background before the real
        // GET /api/designs/:id fetch resolved and the finished image swapped
        // in — read as "showing the old design" rather than "still
        // loading"). Was `rgba(255,255,255,0.72)` by original design (an
        // earlier fix deliberately let the flat render peek through so a
        // genuinely long in-flight render didn't fully block the tile) —
        // changed to fully opaque so no interim/stale content is ever
        // visible while this badge is showing, for all 3 preview types.
        background: '#fff', borderRadius: 4,
      }}
    >
      <div
        style={{
          width: 22, height: 22, borderRadius: '50%',
          border: '3px solid #e2e8f0', borderTopColor: 'var(--selection)',
          animation: 'preview-loading-spin 0.8s linear infinite',
        }}
      />
      <span style={{ fontSize: 11, fontWeight: 600, color: '#374151', background: '#fff', padding: '2px 8px', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}>
        {label}
      </span>
      <style>{'@keyframes preview-loading-spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

function navBtnStyle(side: 'left' | 'right'): React.CSSProperties {
  return {
    position: 'absolute',
    [side]: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 40,
    height: 40,
    borderRadius: '50%',
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#374151',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    zIndex: 1,
  };
}
